// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const separatorStyles = () => css`
    position: relative;
    margin: auto;
    background-color: ${getColor("common.background")};
    padding: 0 1em;
    font-weight: ${x => x.theme.design.typography.weight.bold};
    font-size: ${x => x.theme.design.typography.size.smaller};

    ${breakpoint("lg")} {
        padding: 1em 0;
    }
`;

type OwnProps = WidgetProps & HasShellContext;

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    allowCreateAccount: state.context.settings.settingsCollection.accountSettings.allowCreateAccount,
    allowGuestCheckout:
        state.context.settings.settingsCollection.accountSettings.allowGuestCheckout && !getCurrentUserIsGuest(state),
    /* Valtech Custom Code */
    showGuestCheckout:
        (getReturnUrl(state) && getReturnUrl(state) === getPageLinkByPageType(state, "CheckoutShippingPage")?.url) ||
        props.shellContext.isEditing,
    /* Valtech Custom Code */
});

export interface SignInStyles {
    container?: InjectableCss;
    grid?: GridContainerProps;
    signInGridItem?: GridItemProps;
    separatorGridItem?: GridItemProps;
    separator?: TypographyProps;
    separatorHidden?: TypographyProps;
    createAndGuestGridItem?: GridItemProps;
    spacerGridItem?: GridItemProps;
}

const signInStyles: SignInStyles = {
    container: {},
    grid: {
        gap: 0,
    },
    signInGridItem: {
        width: [12, 12, 4, 4, 4],
    },
    separatorGridItem: {
        width: [12, 12, 1, 1, 1],
        css: css`
            position: relative;
            ${breakpointValues(["1rem 0", null, null, "0"], "padding")}

            &::before {
                content: "";
                position: absolute;
                top: 50%;
                right: 0;
                left: 0;
                height: ${pxToRem(1)};
                background-color: ${getColor("common.border")};

                ${breakpoint("md")} {
                    top: 0;
                    right: auto;
                    bottom: 0;
                    left: 50%;
                    width: ${pxToRem(1)};
                    height: auto;
                }
            }
        `,
    },
    separator: {
        css: separatorStyles(),
    },
    separatorHidden: {
        css: css`
            ${separatorStyles()}
            background-color: transparent;
        `,
    },
    createAndGuestGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            ${breakpoint("md")} {
                display: flex;
                align-items: stretch;
                height: auto;
            }
        `,
    },
};

const styles = signInStyles;

const SignInView: FC<Props> = ({ id, allowCreateAccount, allowGuestCheckout, showGuestCheckout }) => {
    return (
        <StyledWrapper {...styles.container}>
            <GridContainer {...styles.grid}>
                <GridItem {...styles.signInGridItem}>
                    <Zone contentId={id} zoneName="Left" />
                </GridItem>
                {allowCreateAccount && (
                    <>
                        <GridItem {...styles.separatorGridItem}>
                            <Typography {...styles.separator}>{translate("OR")}</Typography>
                        </GridItem>
                        <GridItem {...styles.createAndGuestGridItem}>
                            <Zone contentId={id} zoneName="Middle" />
                        </GridItem>
                    </>
                )}
                {allowGuestCheckout && showGuestCheckout && (
                    <>
                        <GridItem {...styles.separatorGridItem}>
                            <Typography {...styles.separatorHidden}>&nbsp;</Typography>
                        </GridItem>
                        <GridItem {...styles.createAndGuestGridItem}>
                            <Zone contentId={id} zoneName="Right" />
                        </GridItem>
                    </>
                )}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withIsInShell(connect(mapStateToProps)(SignInView)),
    definition: {
        allowedContexts: ["SignInPage"],
        group: "Sign In",
        icon: "table-layout",
    },
};

export default widgetModule;
